package co.maytes.standupbot.dashboard.features.templates

import androidx.compose.runtime.Composable
import co.maytes.standupbot.dashboard.components.widgets.Card
import co.maytes.standupbot.dashboard.components.widgets.cssRgb
import co.maytes.standupbot.dashboard.styles.Motion
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.shared.design.StatusHue
import co.maytes.standupbot.shared.template.SectionKey
import co.maytes.standupbot.shared.template.SectionLabels
import co.maytes.standupbot.shared.template.SectionsConfigDto
import co.maytes.standupbot.shared.validation.SectionsConfigValidator
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.attributes.AttrsScope
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Input
import org.w3c.dom.HTMLElement

@Composable
internal fun MetadataCard(
    name: String,
    onNameChange: (String) -> Unit,
    description: String,
    onDescriptionChange: (String) -> Unit,
    sectionsConfig: SectionsConfigDto,
    onSectionsConfigChange: (SectionsConfigDto) -> Unit,
    sectionsError: String?,
    enabled: Boolean,
) {
    Card(modifier = Modifier.fillMaxWidth()) {
        Column(modifier = Modifier.fillMaxWidth().padding(Spacing.lg).gap(Spacing.md)) {
            CardSectionHeader(title = "Template details", subtitle = "How admins identify this template across the workspace.")
            Row(
                modifier = Modifier.fillMaxWidth().styleModifier {
                    property("gap", "16px")
                    property("flex-wrap", "wrap")
                },
            ) {
                Column(
                    modifier = Modifier.styleModifier {
                        property("flex", "1 1 220px")
                        property("min-width", "0")
                        property("gap", "6px")
                    },
                ) {
                    FieldLabel("Name")
                    Input(type = InputType.Text) {
                        this.value(name)
                        this.placeholder("e.g. Retro-focused")
                        this.onInput { onNameChange(it.value) }
                        if (!enabled) attr("disabled", "disabled")
                        attr("aria-label", "Template name")
                        sharedInputStyle()
                    }
                }
                Column(
                    modifier = Modifier.styleModifier {
                        property("flex", "2 1 320px")
                        property("min-width", "0")
                        property("gap", "6px")
                    },
                ) {
                    FieldLabel("Description")
                    Input(type = InputType.Text) {
                        this.value(description)
                        this.placeholder("Optional — what makes this template distinctive")
                        this.onInput { onDescriptionChange(it.value) }
                        if (!enabled) attr("disabled", "disabled")
                        attr("aria-label", "Template description")
                        sharedInputStyle()
                    }
                }
            }

            DividerLine()

            Column(modifier = Modifier.fillMaxWidth().gap(Spacing.sm)) {
                FieldLabel("Sections in the standup")
                SectionKey.entries.forEach { key ->
                    SectionRow(
                        key = key,
                        config = sectionsConfig,
                        enabled = enabled,
                        onChange = onSectionsConfigChange,
                    )
                }
                if (sectionsError != null) {
                    SpanText(
                        text = sectionsError,
                        modifier = Modifier
                            .fontSize(Typography.caption.size)
                            .color(Palette.status(StatusHue.Danger).solid),
                    )
                }
                SpanText(
                    text = "Turned-off sections are left out of the AI summary and the Slack post. " +
                        "Headings are what your team reads. Applies to every standup that uses this template.",
                    modifier = Modifier
                        .fontSize(Typography.caption.size)
                        .color(Palette.textTertiary),
                )
            }
        }
    }
}

@Composable
private fun SectionRow(
    key: SectionKey,
    config: SectionsConfigDto,
    enabled: Boolean,
    onChange: (SectionsConfigDto) -> Unit,
) {
    val slot = config.slots().first { it.key == key }
    val defaultLabel = key.defaultLabel()
    Row(
        verticalAlignment = Alignment.CenterVertically,
        modifier = Modifier.fillMaxWidth().styleModifier {
            property("gap", "12px")
            property("flex-wrap", "wrap")
        },
    ) {
        Box(modifier = Modifier.styleModifier { property("flex", "0 0 140px") }) {
            SectionPill(
                label = defaultLabel,
                active = slot.included,
                enabled = enabled,
                onToggle = { onChange(config.withIncluded(key, it)) },
            )
        }
        Input(type = InputType.Text) {
            this.value(config.rawLabel(key))
            this.placeholder(defaultLabel)
            this.onInput { onChange(config.withLabel(key, it.value)) }
            if (!enabled || !slot.included) attr("disabled", "disabled")
            attr("maxlength", SectionsConfigValidator.MAX_LABEL_LENGTH.toString())
            attr("aria-label", "$defaultLabel heading")
            sharedInputStyle()
            style {
                property("flex", "1 1 200px")
                property("min-width", "0")
                property("width", "auto")
            }
        }
    }
}

private fun SectionKey.defaultLabel(): String =
    when (this) {
        SectionKey.YESTERDAY -> SectionLabels.YESTERDAY
        SectionKey.TODAY -> SectionLabels.TODAY
        SectionKey.BLOCKERS -> SectionLabels.BLOCKERS
    }

private fun SectionsConfigDto.rawLabel(key: SectionKey): String =
    when (key) {
        SectionKey.YESTERDAY -> yesterdayLabel
        SectionKey.TODAY -> todayLabel
        SectionKey.BLOCKERS -> blockersLabel
    }

@Composable
internal fun DirtyIndicator(isDirty: Boolean, isDefault: Boolean) {
    val text = when {
        isDefault -> "Read-only"
        isDirty -> "Unsaved changes"
        else -> "Up to date"
    }
    val dotColor = when {
        isDefault -> Palette.textTertiary
        isDirty -> Palette.warning
        else -> Palette.success
    }
    Row(
        verticalAlignment = Alignment.CenterVertically,
        modifier = Modifier.styleModifier { property("gap", "8px") },
    ) {
        Box(
            modifier = Modifier
                .width(8.px)
                .styleModifier {
                    property("height", "8px")
                    property("border-radius", "9999px")
                    property("background-color", dotColor.cssRgb())
                },
        )
        SpanText(
            text = text,
            modifier = Modifier
                .fontSize(Typography.caption.size)
                .color(Palette.textSecondary),
        )
    }
}

@Composable
internal fun FieldLabel(text: String) {
    SpanText(
        text = text,
        modifier = Modifier
            .fontSize(Typography.bodySmall.size)
            .fontWeight(500)
            .color(Palette.textPrimary),
    )
}

internal fun AttrsScope<HTMLElement>.sharedInputStyle() {
    style {
        property("width", "100%")
        property("padding", "12px 14px")
        property("border", "1px solid ${Palette.border.cssRgb()}")
        property("border-radius", "10px")
        property("font-size", "14.5px")
        property("line-height", "1.4")
        property("font-family", Typography.FONT_SANS)
        property("background-color", Palette.surface.cssRgb())
        property("color", Palette.textPrimary.cssRgb())
        property("outline", "none")
        property("transition", Motion.transition(Motion.DUR_FAST, "border-color"))
    }
}
